// Data model: SPEC §8. Soft-deletable entities carry `deletedAt`
// (DESIGN_SYSTEM §5: undo over confirm, purged after 24 h).

export const STYLE_IDS = [
  'oldschool', 'golden', 'tribe', 'premier', 'peterock', 'wu', 'qb', 'gfunk', 'south', 'memphis',
  'chipmunk', 'dilla', 'madvillain', 'jazz', 'griselda', 'trap', 'drill', 'phonk', 'cloud', 'rage', 'melodic',
] as const;
export type StyleId = (typeof STYLE_IDS)[number];

export type DrumFlavor = 'crisp' | 'punch' | 'dusty' | 'grit' | 'soft' | 'clean' | 'clip' | 'minimal';
export type Vintage = 'SP1200' | 'MPC60' | 'MPC3000' | 'Vinyl' | 'Tape' | '';
export type LteSource = 'upright' | 'electric' | 'keys' | 'band' | 'strings' | 'synth';
export type LteGoal = 'warm' | 'punch' | 'deep';
export type Role = 'kick' | '808' | 'snare' | 'clap' | 'hat' | 'hat_open' | 'perc';
export type SampleType = 'kick' | 'snare' | 'clap' | 'hat_closed' | 'hat_open' | 'rim' | 'perc' | '808' | 'melodic' | 'fx' | 'unknown';
/** v0.5.1 (SPEC §8, F3): what kind of perc a perc sample is. */
export type PercKind = 'shaker' | 'tambourine' | 'conga' | 'bongo' | 'hand' | 'cowbell' | 'bell' | 'wood' | 'guiro' | 'other';

/** SPEC §8. Extracted from the reference (SPEC §9); never hand-typed. */
export interface Style {
  id: StyleId; name: string; sub: string; group: 'classic' | 'modern'; tags: ('sample' | '808')[];
  lufs: string; tempo: string; drums: string; breaks: string; kit: string; crate: string; keys: string;
  melodic: string; lowend: string; low: string; gear: string; tip: string; move: string; refs: string;
  comp: string; vocal: string; fx: string; master: string; tone: string;
  bpmDefault: number; swingDefault: number;
  mpc: { flavor: DrumFlavor; vintage: Vintage };
  lte: { source: LteSource; goal: LteGoal };
  crateKeywords: string[];
}

/** The header's style picker only needs this much (SPEC §9: keeps the full Style data in the lazy Learn chunk). */
export interface StyleIndexEntry {
  id: StyleId;
  name: string;
  sub: string;
  group: 'classic' | 'modern';
  bpmDefault: number;
  swingDefault: number;
  /** The style's drum flavor, for sample fit scoring (SPEC F3); from the reference, never hand-typed. */
  flavor: DrumFlavor;
  /** SPEC Appendix A: the dig algorithm boosts records matching these (SPEC F6). */
  crateKeywords: string[];
}

export interface SoftDeletable {
  deletedAt?: number;
}

export interface SampleAnalysis {
  durationMs: number; peakDb: number; rmsDb: number; centroidHz: number; flatness: number;
  bands: { sub: number; low: number; lowMid: number; mid: number; high: number };
  /** Largest per-frame share above 4 kHz among frames within 30 dB of the loudest. Added in analysis v3, so absent on older rows. */
  highPeak?: number;
  attackMs: number; decayMs: number; onsets60ms: number; pitchHz?: number; note?: string; pitchConf?: number;
  version: number;
}

export interface Sample extends SoftDeletable {
  id: string; hash: string; name: string; blobKey: string; sizeBytes: number; createdAt: number;
  type: SampleType; typeSource: 'rule' | 'hint' | 'user'; tags: string[]; favourite: boolean;
  /** v0.5.1: only when type is 'perc'. Optional and not indexed, so no Dexie bump; a missing kind is backfilled at start. */
  percKind?: PercKind; percKindSource?: 'hint' | 'user';
  /** v0.6 (F14): the key you entered for a melodic sample (root 0 to 11, minor or major). Optional and not indexed, so no Dexie bump. Detection arrives in v1.6 (keySource 'detected'). */
  keyRoot?: number; minor?: boolean; keySource?: 'user';
  analysis: SampleAnalysis;
}

/** The audio bytes and MIME type, stored as an ArrayBuffer rather than a Blob (DECISIONS): WebKit's IndexedDB can't structured-clone a Blob into an object store. */
export interface SampleBlob {
  key: string;
  bytes: ArrayBuffer;
  mime: string;
}

export interface PadSettings {
  sampleId?: string; role?: Role; level: number; pan: number; tuneSemi: number;
  tuneCents: number; decay: number; lpfHz?: number; choke?: number;
  /** v0.5.1: the kind a perc pad prefers (SPEC F4). Absent = any perc. */
  percKind?: PercKind;
}
export interface Kit extends SoftDeletable {
  id: string; name: string; pads: PadSettings[]; styleId?: StyleId; projectId?: string; updatedAt: number;
}

export type Cell = '.' | 'x' | 'X' | 'g' | 'r' | 't';
export type LaneId = 'K' | '8' | 'S' | 'C' | 'R' | 'H' | 'O' | 'P' | 'B';
export interface Pattern extends SoftDeletable {
  id: string; styleId: StyleId; bars: 1 | 2; bpm: number; swing: number; humanize: boolean;
  dusty: boolean; lanes: Partial<Record<LaneId, Cell[]>>; laneToPad: Partial<Record<LaneId, number>>; projectId?: string;
  /** Dusty low-pass cutoff in Hz (SPEC 0.3.14). Optional and not indexed, so no Dexie bump: a missing value reads as the reference's 8500 Hz. */
  dustyHz?: number;
}

export interface Channel {
  localId?: number;
  id?: string; handle?: string; name: string; styles: StyleId[]; genres: string[];
  weight: 1 | 2 | 3 | 4; enabled: boolean;
  /** `new` channels are hidden from digs unless Include new recordings is on (SPEC F6). Default `mixed`. */
  era?: 'reissue' | 'new' | 'mixed';
  /** A tombstone: hides a shipped channel with this id from the app (SPEC F6). Not indexed. */
  deleted?: boolean;
}
export interface HistoryEntry {
  seq?: number;
  videoId: string; at: number; styleId: StyleId; outcome?: 'skipped' | 'played' | 'marked' | 'bad';
}
/** Per-record state kept locally (SPEC F6): heard, bad after error 100/101/150, favourite. */
export interface RecordState {
  videoId: string;
  bad?: boolean;
  /** Marked "Not a record" from Dig, with the reason. Acts like `bad` (never dug again). A plain field: no Dexie bump. */
  notRecord?: 'talk' | 'new-session' | 'cover' | 'other';
  favourite?: boolean;
  /** Last time it was dug or played, in ms */
  heardAt?: number;
}

/** A style's crate shard cached for offline use (SPEC F6). The shard is opaque here; src/lib/dig.ts types it. */
export interface CrateCache {
  styleId: StyleId;
  shard: unknown;
  cachedAt: number;
}

export interface Mark extends SoftDeletable {
  id: string; videoId: string; inS: number; outS: number; bpm?: number;
  keyRoot?: number; minor?: boolean; kind: 'loop' | 'stab' | 'bass' | 'vocal' | 'break'; note?: string;
  projectId?: string; createdAt: number;
}

export interface Project extends SoftDeletable {
  id: string; name: string; styleId: StyleId; bpm: number;
  /** A new project has no key until picked (SPEC 0.3.21). */
  keyRoot?: number; minor?: boolean;
  kitId?: string; patternId?: string; markIds: string[]; checks: Record<string, boolean>; notes: string;
  step: number; archived: boolean; createdAt: number; updatedAt: number;
  /** v0.6 (F10 base): set when a check-out says "done", meaning bounced on the MPC. Optional and unindexed. */
  finishedAt?: number;
}

export interface VerifyMark { status: 'ok' | 'differs' | 'unchecked'; note?: string; at: number }

export type Theme = 'system' | 'light' | 'dark';

export interface Settings {
  key: 'app';
  theme: Theme; styleId: StyleId; activeProjectId?: string;
  learnMode: 'build' | 'mix'; lastBackupAt?: number; region?: string;
  /** v1.0 (F8): when your data last changed, and when it first did. They drive the 14-day backup reminder. Optional and unindexed, so no Dexie bump. */
  lastChangeAt?: number; firstChangeAt?: number;
  /** v1.0 (F8, SPEC §14): what you ticked on the Verify on MPC checklist, by item id ('v01'…'v13'). Optional and unindexed, so no Dexie bump. */
  verify?: Record<string, VerifyMark>;
  /** Android only: navigator.vibrate(10) on pad press and mark saved (DESIGN_SYSTEM §5). */
  haptics: boolean;
  /** Dig: include records heard in the last 90 days (SPEC F6). */
  rediscover: boolean;
  /** Dig by genre (SPEC F6, v1.1). Optional and unindexed, so no Dexie bump. */
  digSource?: 'style' | 'genres';
  digGenres?: string[];
  includeNew?: boolean;
  /** A channel id the next digs are pinned to ("Stay on this channel") */
  pinnedChannel?: string;
}

export type TabId = 'dig' | 'drums' | 'beat' | 'learn' | 'more';

/** Where you left off (DESIGN_SYSTEM §5: continuity). */
export interface Continuity {
  key: 'app';
  lastPath: string;
  tabPaths: Partial<Record<TabId, string>>;
  scroll: Record<string, number>;
  lastRecord?: { videoId: string; atS: number };
  /** Learn > Low End Theory builder (SPEC F2). `forStyle` lets the screen tell a stale value (a previous style's pick) apart from a deliberate choice for the current style, and reset to that style's own preset - done locally by the screen, so the global store never needs the full (lazy-loaded) style data. */
  builder?: { source: LteSource; goal: LteGoal; forStyle: StyleId };
  /** Learn > Tempo & FX calculator (SPEC F2). */
  tempoBpm?: number;
  /** Chord match key-by-ear drone loudness, 0 to 1 (SPEC F14). Optional, so no Dexie bump. */
  droneLevel?: number;
  /** v0.6 (F10): the id of the last session whose check-out was shown, answered or skipped, so it shows at most once per session. Optional, so no Dexie change. */
  checkoutSeen?: string;
  /** Learn > Low End Theory note finder (SPEC F2). */
  tuner?: { note: number; oct: number };
  /** Learn > EQ trainer (SPEC F15): the knobs, the selected band and the loaded preset. */
  eqTrainer?: EqTrainerState;
  /** Learn > Compressor trainer (SPEC F16 v0.3.1): the knobs, the source, the view, the selected control page and the loaded preset. Optional field, no Dexie version bump (like eqTrainer). */
  compTrainer?: CompTrainerState;
  /** Drums > Library (SPEC F3, v0.4): the chip, sheet filters and sort, kept across a relaunch. Optional field, no Dexie version bump (like eqTrainer). */
  library?: LibraryContinuity;
}

// v0.4 (F3): Drums library filters and sort (SPEC §8)
export type LibraryChipId = 'all' | 'kick' | '808' | 'snare' | 'rim' | 'clap' | 'hat' | 'perc' | 'melodic' | 'fx' | 'unknown';
export type LibrarySortId = 'fit' | 'name' | 'date';
/** `kind` (v0.5.1) narrows the Perc chip; absent = all perc. */
export interface LibraryFilters { chip: LibraryChipId; favourites: boolean; tag?: string; minFit?: number; key?: string; kind?: PercKind }
export interface LibraryContinuity { filters: LibraryFilters; sort: LibrarySortId; }

// v0.2 (F15): EQ trainer (SPEC §8)
export type EqBandId = 'lowCut' | 'low' | 'lowMid' | 'highMid' | 'high' | 'highCut';
export type EqBandType = 'bell' | 'lowShelf' | 'highShelf' | 'highPass' | 'lowPass';
export interface EqBand {
  id: EqBandId; on: boolean; type: EqBandType; freqHz: number; gainDb: number; q: number;
  /** LOW CUT / HIGH CUT only */
  slopeDb?: 6 | 12 | 18 | 24;
}
export interface EqPreset {
  id: string; label: string; source: string; bands: EqBand[]; highCutFromStyle?: boolean;
}
export interface EqZone { label: string; fromHz: number; toHz: number; }

export interface EqTrainerState { bands: EqBand[]; selected: EqBandId; presetId?: string; }

// v0.3.1 (F16): Compressor trainer, "see it" (SPEC §8)
export interface CompSettings {
  thresholdDb: number; ratio: number; attackMs: number; releaseMs: number;
  /** 0-100 %, continuous like the unit's Knee knob. Before the 2026-10 Verify on MPC pass this was 'hard' | 'soft'; saved values are read through normaliseCompSettings. */
  knee: number;
  /** The unit's OUTPUT knob (0-30 dB): the makeup gain. */
  makeupDb: number;
  /** 0-1. */
  mix: number;
}
export type CompDemoSource = 'loop' | 'kick' | 'snare' | '808';
export interface CompPreset {
  id: string; label: string; source: string; // source: the comp.json row
  settings: Omit<CompSettings, 'thresholdDb'>;
  grTargetDb: [number, number];
  demoSource?: CompDemoSource;
  needs?: 'ownSample' | 'sidechain';
  provisional?: string[];
}
export interface LevelStats { peakDb: number; rmsDb: number; crestDb: number; }
export interface CompResult { out: Float32Array; grDb: Float32Array; before: LevelStats; after: LevelStats; maxGrDb: number; }

export type CompControlPage = 'threshold' | 'knee';
export type CompView = 'loop' | 'onehit' | 'level';
export interface CompTrainerState {
  settings: CompSettings;
  source: CompDemoSource;
  view: CompView;
  page: CompControlPage;
  presetId?: string;
}

// v0.6 (SPEC §8, F14): Chord match. harmony.json and the results of src/data/logic/harmony.ts
export type Quality = 'maj' | 'min';
export interface Chord { root: number; quality: Quality }
export interface HarmonyRule {
  fixed: Quality; moved: Quality; interval: number; role: string; strength: 1 | 2 | 3; mood: string;
  /** One plain line, in our own words, for the expanded row. */
  why: string;
}
export interface HarmonyData {
  version: number;
  rules: HarmonyRule[];
  styleMoods: Partial<Record<StyleId, string[]>>;
  /** Styles where shifting up scores a +1 bonus (shifting down always does). */
  upBonus: StyleId[];
  /** [largest |shift|, cost] pairs, in order: the first pair whose limit covers the shift. */
  shiftCost: [number, number][];
  /** Same root, other quality: always flagged, Sequence only. */
  mixture: { role: string; mood: string; why: string };
}
export interface ChordSuggestion {
  shift: number; chord: Chord; name: string; role: string; mood: string;
  sharedNotes: number[]; layer: 'ok' | 'sequence'; score: number;
  speedFactor: number; bpmAfter?: number; lengthPct: number; mpcHint: string;
  /** The rule's plain line, for the expanded row. */
  why: string;
}

/** One chord that goes with a picked chord (Chord match with one chord, SPEC F14). A set of options, never a progression. */
export interface RelatedChord {
  chord: Chord; name: string; role: string; mood: string;
  sharedNotes: number[]; layer: 'ok' | 'sequence';
  /** True for the same root with the other quality (modal mixture). */
  mixture: boolean;
  why: string;
}

// v0.6 (SPEC §8, F10 base): the session journal. A session is logged automatically while you work in a project;
// the check-out (outcome, optional energy, and for stuck a phase and a note) is filled in afterwards.
// `Project.checks` is keyed by the stable step ids (w01 to w14). `cardIds` joins in v1.4.
export type SessionOutcome = 'progress' | 'stuck' | 'done';
export type SessionEnergy = 'low' | 'ok' | 'flowing';
export interface Session {
  id: string; projectId: string; startedAt: number; endedAt?: number;
  stepIds: string[]; phaseIds: string[];
  energy?: SessionEnergy; outcome?: SessionOutcome; stuckPhaseId?: string; stuckNote?: string;
}

// v1.1 (SPEC F6 Chop preview, §8): a cue is one timestamp on a record, set from a tap. Dexie v5 table `cues`, keyed
// [videoId+pad]. pad is 0..15 (A01 = 0, A16 = 15); atS is seconds in 0..duration.
export interface Cue { videoId: string; pad: number; atS: number; }

// v1.1 (SPEC F15, F16): training results, Dexie v5 tables `eqTraining` and `compTraining`. Empty until the trainers ship.
export interface EqTrainingResult {
  id: string; mode: 'match' | 'picture' | 'blind'; level: number; targetHz?: number;
  answerHz?: number; errorDb?: number; correct: boolean; at: number;
  /** Optional fields added when the trainer shipped (unindexed, no Dexie bump): the 10-question session, a cut question, the spectrum band asked about. */
  session?: string; cut?: boolean; band?: string;
}
export interface CompTrainingResult {
  id: string; mode: 'attack' | 'amount' | 'match'; level: number; errorDb?: number; correct: boolean; at: number;
  session?: string;
}
